import { useState, useEffect } from "react";
import React from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  CircularProgress,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import SwitchToggle from "../../components/SwitchToggle";
import { VALUE_TYPE, DISCOUNT_OPTION, DISCOUNT_LIMITS } from "../../constants/discount";
import { useGetCoupon, useCreateCoupon, useUpdateCoupon } from "../../api/hooks/useCoupon";
import { showSuccess, showError } from "../../helpers/toast";
import type { CreateCouponPayload, UpdateCouponPayload } from "../../api/types/coupon.types";
import { isFloat, isNumber } from "../../helpers/validations";

const AddDiscount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const couponId = location?.state?.couponId;

  const isEditMode = !!couponId;

  // Form states
  const [name, setName] = useState("");
  const [type, setType] = useState<number>(VALUE_TYPE.FLAT);
  const [discountValue, setDiscountValue] = useState("");
  const [newUserOnly, setNewUserOnly] = useState<boolean>(false);
  const [discountOption, setDiscountOption] = useState<number>(DISCOUNT_OPTION.ONE_TIME);
  const [expiryDate, setExpiryDate] = useState<Dayjs | null>(null);
  const [usageLimit, setUsageLimit] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expiryDateError, setExpiryDateError] = useState<string>("");

  // Fetch coupon data for edit mode
  const { data: couponData, isLoading: isLoadingCoupon, error: couponError } = useGetCoupon(
    couponId || '',
    { enabled: isEditMode }
  );

  // Update form fields when coupon data is loaded
  useEffect(() => {
    if (couponData?.data) {
      const coupon = couponData.data;
      setName(coupon.name || "");
      setType(coupon.valueType || VALUE_TYPE.FLAT);
      setDiscountValue(coupon.value?.toString() || "");
      setNewUserOnly(coupon.newUserOnly || false);
      setDiscountOption(coupon.option || DISCOUNT_OPTION.ONE_TIME);
      setUsageLimit(coupon.usageLimit?.toString() || "");
      setExpiryDate(coupon.expiryDate ? dayjs(coupon.expiryDate) : null);
    }
  }, [couponData]);

  // Handle error
  useEffect(() => {
    if (couponError) {
      showError((couponError as any)?.response?.data?.message || "Failed to load coupon details");
    }
  }, [couponError]);

  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(Number(event.target.value));
    setDiscountValue(""); // Reset value when type changes
  };

  const handleDiscountOptionChange = (event: SelectChangeEvent) => {
    setDiscountOption(Number(event.target.value));
  };

  // Create coupon mutation
  const { mutate: createCoupon, isPending: isCreating } = useCreateCoupon({
    onSuccess: (response) => {
      showSuccess(response.message || "Coupon created successfully");
      navigate("/discounts");
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to create coupon");
    },
  });

  // Update coupon mutation
  const { mutate: updateCoupon, isPending: isUpdating } = useUpdateCoupon({
    onSuccess: (response) => {
      showSuccess(response.message || "Coupon updated successfully");
      navigate("/discounts");
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update coupon");
    },
  });

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = "Coupon name is required";
    }

    if (!discountValue.trim()) {
      newErrors.discountValue = `${type === VALUE_TYPE.FLAT ? "Flat amount" : "Discount percentage"} is required`;
    } else {
      // Check for consecutive leading zeros (like 0000) but allow valid numbers like 1000
      if (/^0+$/.test(discountValue) || /^0+[1-9]/.test(discountValue)) {
        newErrors.discountValue = "Please enter a valid number";
      } else {
        // Check decimal places (max 2)
        const decimalParts = discountValue.split(".");
        if (decimalParts.length > 1 && decimalParts[1].length > 2) {
          newErrors.discountValue = "Maximum 2 decimal places allowed";
        } else {
          const numValue = Number(discountValue);
          if (isNaN(numValue) || numValue <= 0) {
            newErrors.discountValue = "Please enter a valid positive number greater than 0";
          } else if (numValue === 0) {
            newErrors.discountValue = "Value cannot be 0";
          } else if (type === VALUE_TYPE.PERCENT && numValue > DISCOUNT_LIMITS.MAX_PERCENTAGE) {
            newErrors.discountValue = `Discount percentage cannot exceed ${DISCOUNT_LIMITS.MAX_PERCENTAGE}%`;
          } else if (type === VALUE_TYPE.FLAT && numValue > DISCOUNT_LIMITS.MAX_FLAT_AMOUNT) {
            newErrors.discountValue = `Flat amount cannot exceed $${DISCOUNT_LIMITS.MAX_FLAT_AMOUNT.toLocaleString()}`;
          }
        }
      }
    }

    if (!usageLimit.trim()) {
      newErrors.usageLimit = "Usage limit is required";
    } else {
      const numLimit = Number(usageLimit);
      if (isNaN(numLimit) || numLimit <= 0 || !Number.isInteger(numLimit)) {
        newErrors.usageLimit = "Please enter a valid positive integer";
      }
    }

    // Validate expiry date - must be today or future date
    if (expiryDate) {
      const today = dayjs().startOf('day');
      const selectedDate = expiryDate.startOf('day');
      if (selectedDate.isBefore(today)) {
        newErrors.expiryDate = "Expiry date cannot be in the past";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const payload: CreateCouponPayload | UpdateCouponPayload = {
      name: name.trim(),
      value: Number(discountValue),
      valueType: type,
      usageLimit: Number(usageLimit),
      expiryDate: expiryDate ? expiryDate.format('YYYY-MM-DD') : "",
      option: discountOption,
      newUserOnly: newUserOnly,
    };

    if (isEditMode) {
      // For update, include status if available
      const updatePayload: UpdateCouponPayload = {
        ...payload,
        status: couponData?.data?.status ?? true,
      };
      updateCoupon({ id: couponId, data: updatePayload });
    } else {
      createCoupon(payload);
    }
  };

  if (isEditMode && isLoadingCoupon) {
    return (
      <>
        <div className="main_title">
          <h1>Edit Discount and Coupon</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
            <button onClick={() => navigate("/discounts")}>
              Discounts and Coupons
            </button>{" "}
            - Edit Discount and Coupon
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  const isLoading = isCreating || isUpdating;

  return (
    <>
      <div className="main_title">
        <h1>{isEditMode ? "Edit" : "Add"} Discount and Coupon</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          <button onClick={() => navigate("/discounts")}>
            Discounts and Coupons
          </button>{" "}
          - {isEditMode ? "Edit" : "Add"} Discount and Coupon
        </p>
      </div>

      <div className="cards custom_form_details">
        <div className="form">
          <form onSubmit={handleSubmit}>
            <div className="gap_m w_100">
              {/* Discount Name */}
              <div className="control_group w_50">
                <label htmlFor="offer_name">Offer/Discount Name <span className="req">*</span></label>
                <TextField
                  id="offer_name"
                  placeholder="Enter Discount Name"
                  fullWidth
                  hiddenLabel
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) {
                      setErrors({ ...errors, name: "" });
                    }
                  }}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Coupon Code */}
              <div className="control_group w_50">
                <label htmlFor="coupon_code">Coupon Code</label>
                <TextField
                  disabled
                  id="coupon_code"
                  placeholder={
                    isEditMode && (couponData?.data?.code || couponData?.data?.couponCode)
                      ? couponData?.data?.code || couponData?.data?.couponCode
                      : "Will be auto-generated"
                  }
                  fullWidth
                  hiddenLabel
                  value={
                    isEditMode && (couponData?.data?.code || couponData?.data?.couponCode)
                      ? couponData?.data?.code || couponData?.data?.couponCode || ""
                      : ""
                  }
                />
              </div>

              {/* Discount Type */}
              <div className="control_group w_25">
                <label>Discount Type</label>
                <Select
                  fullWidth
                  value={type.toString()}
                  onChange={handleTypeChange}
                  disabled={isLoading}
                >
                  <MenuItem value={VALUE_TYPE.FLAT.toString()}>Flat ($)</MenuItem>
                  <MenuItem value={VALUE_TYPE.PERCENT.toString()}>Percentage (%)</MenuItem>
                </Select>
              </div>

              {/* Discount Value (Dynamic label based on type) */}
              <div className="control_group w_25">
                <label>
                  {type === VALUE_TYPE.FLAT ? "Flat Amount ($)" : "Discount (%)"} <span className="req">*</span>
                </label>
                <TextField
                  placeholder={type === VALUE_TYPE.FLAT ? "Enter Flat Amount" : "Enter Discount (%)"}
                  fullWidth
                  hiddenLabel
                  value={discountValue}
                  inputProps={{
                    maxLength: type === VALUE_TYPE.PERCENT ? 6 : 10, // Allow up to 100.00 for percent, 10000.00 for flat
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string
                    if (value === "") {
                      setDiscountValue("");
                      if (errors.discountValue) {
                        setErrors({ ...errors, discountValue: "" });
                      }
                      return;
                    }
                    // Check if it's a valid float format
                    if (isFloat(value)) {
                      // Restrict to 2 decimal places
                      const decimalParts = value.split(".");
                      if (decimalParts.length > 1 && decimalParts[1].length > 2) {
                        return; // Don't allow more than 2 decimal places
                      }
                      // Prevent consecutive leading zeros (like 0000, 0001) but allow:
                      // - Single 0 (for typing 0.2)
                      // - 0. followed by digits (0.2, 0.5)
                      // - Numbers starting with 1-9 (1, 10, 100, 1000, 1.5, etc.)
                      const isConsecutiveZeros = /^0+$/.test(value) || /^0+[1-9]/.test(value);
                      if (!isConsecutiveZeros || value === "0" || /^0\./.test(value)) {
                        setDiscountValue(value);
                        if (errors.discountValue) {
                          setErrors({ ...errors, discountValue: "" });
                        }
                      }
                    }
                  }}
                  type="string"
                  error={!!errors.discountValue}
                  helperText={errors.discountValue}
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Discount Option */}
              <div className="control_group w_50">
                <label>Discount Option</label>
                <Select
                  fullWidth
                  value={discountOption.toString()}
                  onChange={handleDiscountOptionChange}
                  disabled={isLoading}
                >
                  <MenuItem value={DISCOUNT_OPTION.ONE_TIME.toString()}>One Time</MenuItem>
                  <MenuItem value={DISCOUNT_OPTION.RECURRING.toString()}>Recurring</MenuItem>
                </Select>
              </div>

              {/* Expiry Date */}
              <div className="control_group w_50">
                <label>Expiry Date</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className="form_control"
                    value={expiryDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        const today = dayjs().startOf('day');
                        const selectedDate = newValue.startOf('day');
                        if (selectedDate.isBefore(today)) {
                          setExpiryDateError("Expiry date cannot be in the past");
                          setExpiryDate(null);
                        } else {
                          setExpiryDate(newValue);
                          setExpiryDateError("");
                          if (errors.expiryDate) {
                            setErrors({ ...errors, expiryDate: "" });
                          }
                        }
                      } else {
                        setExpiryDate(null);
                        setExpiryDateError("");
                      }
                    }}
                    format="DD-MM-YYYY"
                    minDate={dayjs()}
                    shouldDisableDate={(date) => {
                      return date.isBefore(dayjs().startOf('day'));
                    }}
                    disabled={isLoading}
                    slots={{
                      openPickerIcon: () => (
                        <img src="/static/images/date_icon.svg" alt="Pick date" />
                      ),
                    }}
                    slotProps={{
                      textField: {
                        placeholder: "Select Date",
                        fullWidth: true,
                        hiddenLabel: true,
                        error: !!errors.expiryDate || !!expiryDateError,
                        helperText: errors.expiryDate || expiryDateError,
                      },
                    }}
                    desktopModeMediaQuery="(min-width:0px)"
                  />
                </LocalizationProvider>
                {errors.expiryDate && !expiryDateError && (
                  <h6 className="err_msg">{errors.expiryDate}</h6>
                )}
              </div>

              {/* Usage Limit */}
              <div className="control_group w_50">
                <label>Usage Limit <span className="req">*</span></label>
                <TextField
                  type="number"
                  placeholder="e.g., 10"
                  fullWidth
                  value={usageLimit}
                  onChange={(e) => {
                    if(isNumber(e.target.value)) {
                      setUsageLimit(e.target.value);
                      if (errors.usageLimit) {
                        setErrors({ ...errors, usageLimit: "" });
                      }
                    }
                  }}
                  error={!!errors.usageLimit}
                  helperText={errors.usageLimit}
                  required
                  disabled={isLoading}
                />
              </div>

              {/* New User Only Switch */}
              <div className="control_group w_50">
                <label>New User Only</label>
                <SwitchToggle
                  checked={newUserOnly}
                  onChange={(e) => setNewUserOnly(e.target.checked)}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                variant="contained"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (isEditMode ? "Updating..." : "Creating...") : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddDiscount;
